import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getFlows, 
  createFlow, 
  getProject, 
  deleteFlow 
} from '../services/api';
import { 
  Zap, 
  Plus, 
  ArrowLeft, 
  Calendar, 
  MoreVertical, 
  Edit3, 
  Trash2,
  Box,
  ArrowRight,
  Play
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const ComposerFlowsList = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [project, setProject] = useState(null);
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  useEffect(() => {
    loadData();
  }, [projectId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectData, flowsData] = await Promise.all([
        getProject(projectId),
        getFlows(projectId)
      ]);
      setProject(projectData);
      setFlows(flowsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFlow = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      setCreating(true);
      const newFlow = await createFlow({
        name: formData.name,
        description: formData.description,
        projectId,
        nodes: [],
        connections: []
      });
      
      setFormData({ name: '', description: '' });
      setShowCreateForm(false);
      
      // Navigate to the new flow
      navigate(`/projects/${projectId}/composer/${newFlow.id}`);
    } catch (error) {
      console.error('Error creating flow:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteFlow = async (flowId) => {
    if (!window.confirm('Are you sure you want to delete this flow? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteFlow(flowId);
      loadData(); // Reload the list
    } catch (error) {
      console.error('Error deleting flow:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading flows...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-100 rounded-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <nav className="flex items-center space-x-2 text-sm text-gray-600">
                <button
                  onClick={() => navigate(`/projects/${projectId}`)}
                  className="flex items-center hover:text-celadon-600 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to Project
                </button>
              </nav>
              
              {/* Icon */}
              <div className="bg-gradient-to-br from-celadon-50 to-celadon-100 p-4 rounded-2xl shadow-soft">
                <Zap className="w-8 h-8 text-celadon-600" />
              </div>
              
              {/* Title and Description */}
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Composer Flows</h1>
                <p className="text-gray-600 mt-1">
                  Visual prompt engineering workflows for {project?.name}
                </p>
                {flows.length > 0 && (
                  <div className="flex items-center mt-3 text-sm text-gray-500">
                    <Zap className="w-4 h-4 mr-1" />
                    {flows.length} flow{flows.length !== 1 ? 's' : ''} created
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Button */}
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-6 py-3 bg-celadon-600 text-white font-medium rounded-xl hover:bg-celadon-700 focus:outline-none focus:ring-2 focus:ring-celadon-500 focus:ring-offset-2 shadow-soft hover:shadow-medium transition-all duration-200 transform hover:scale-105"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Flow
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {flows.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <Zap className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No flows yet</h3>
            <p className="text-gray-500 mb-6 max-w-md mx-auto">
              Create your first visual prompt engineering workflow to start building complex LLM applications.
            </p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-celadon-600 text-white px-6 py-3 rounded-lg hover:bg-celadon-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2 inline" />
              Create First Flow
            </button>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {flows.map((flow) => (
              <div
                key={flow.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 group overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-celadon-100 rounded-lg">
                        <Zap className="h-5 w-5 text-celadon-700" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {flow.name}
                        </h3>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {/* Quick Actions */}
                      <button
                        onClick={() => navigate(`/projects/${projectId}/composer/${flow.id}`)}
                        className="p-2 text-gray-400 hover:text-celadon-600 hover:bg-celadon-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteFlow(flow.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {flow.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {flow.description}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <Box className="h-4 w-4 mr-1" />
                        {flow.node_count || 0} nodes
                      </div>
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDistanceToNow(new Date(flow.updated_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/projects/${projectId}/composer/${flow.id}`)}
                      className="flex-1 bg-celadon-600 text-white text-sm font-medium py-2 px-4 rounded-lg hover:bg-celadon-700 transition-colors flex items-center justify-center"
                    >
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit Flow
                    </button>
                    
                    <button className="px-4 py-2 text-celadon-600 bg-celadon-50 text-sm font-medium rounded-lg hover:bg-celadon-100 transition-colors flex items-center">
                      <Play className="h-4 w-4 mr-1" />
                      Run
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Flow Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg bg-white rounded-lg shadow-lg">
            <div className="mt-3">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-celadon-100 rounded-xl">
                  <Zap className="h-6 w-6 text-celadon-700" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Create New Flow
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    Design a visual prompt engineering workflow
                  </p>
                </div>
              </div>
              
              <form onSubmit={handleCreateFlow} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Flow Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Content Generation Pipeline"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-500"
                    required
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this flow does..."
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-celadon-500"
                  />
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    disabled={creating}
                    className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating || !formData.name.trim()}
                    className="px-4 py-2 bg-celadon-600 text-white rounded-md hover:bg-celadon-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {creating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Create Flow
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposerFlowsList; 